import { useState } from "react";
import { motion } from "framer-motion";
import { Brain, Cpu, Network, FolderTree, ShieldCheck, Rocket, Loader2 } from "lucide-react";
import HudFrame from "@/components/primitives/HudFrame";
import { toast } from "sonner";
import { apiCreateCheckoutSession } from "@/lib/api";

const ICONS = {
    brain: Brain,
    chip: Cpu,
    network: Network,
    folder: FolderTree,
    shield: ShieldCheck,
    rocket: Rocket,
};

export default function ModuleCard({ module: m, index }) {
    const Icon = ICONS[m.icon] || Brain;
    const [busy, setBusy] = useState(null); // null | sku string currently in flight

    const startCheckout = async (sku, variantId) => {
        if (busy) return;
        setBusy(sku);
        try {
            const payload = { sku };
            if (variantId) payload.module_variant = variantId;
            const { url } = await apiCreateCheckoutSession(payload);
            window.location.href = url;
        } catch (err) {
            setBusy(null);
            toast.error(err?.response?.data?.detail || "Checkout failed");
        }
    };

    const hasVariants = Array.isArray(m.variants) && m.variants.length > 0;

    return (
        <motion.div
            data-testid={`module-card-${m.id}`}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
                duration: 0.6,
                delay: 0.06 * index,
                ease: [0.22, 1, 0.36, 1],
            }}
            className="group relative h-full"
        >
            <HudFrame
                bracketColor="var(--tca-cyan-core)"
                bracketSize={12}
                borderTint="rgba(0, 224, 232, 0.10)"
                className="
                    relative h-full bg-tca-bg-panel/55 backdrop-blur-md
                    hover:bg-tca-bg-panel/85 hover:scale-[1.015]
                    transition-all duration-500 ease-out
                "
                innerClassName="p-6 sm:p-7 flex flex-col h-full min-h-[260px]"
            >
                <div className="flex items-center justify-between mb-5">
                    <div className="relative">
                        <Icon
                            size={28}
                            strokeWidth={1.4}
                            className="text-tca-cyan group-hover:text-tca-cyan-hi transition-colors"
                            style={{ filter: "drop-shadow(0 0 8px rgba(0, 224, 232, 0.35))" }}
                        />
                    </div>
                    <span className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-ink-muted/65">
                        Module · 0{index + 1}
                    </span>
                </div>

                <div className="font-display text-[22px] sm:text-2xl text-tca-ink leading-tight">
                    {m.name}
                </div>

                <div className="mt-2 font-mono-tight text-tca-cyan text-xl sm:text-[22px] tracking-[-0.01em]">
                    {m.price}
                </div>

                <p className="mt-4 text-sm leading-snug text-tca-ink-muted flex-1">
                    {m.desc}
                </p>

                {hasVariants ? (
                    <div className="mt-6 grid grid-cols-2 gap-2">
                        {m.variants.map((v) => (
                            <button
                                key={v.id}
                                type="button"
                                data-testid={`module-cta-${m.id}-${v.id}`}
                                disabled={busy === v.sku}
                                onClick={() => startCheckout(v.sku, v.id)}
                                className="
                                    inline-flex items-center justify-center gap-1.5
                                    font-mono-tight font-semibold uppercase tracking-[0.16em] text-[10px]
                                    py-2.5 border border-tca-cyan/55 text-tca-cyan
                                    bg-gradient-to-b from-[rgba(0,219,255,0.10)] to-transparent
                                    hover:text-tca-cyan-hi hover:border-tca-cyan hover:shadow-glow-cyan-xs
                                    rounded-full transition-all duration-300
                                    disabled:opacity-60 disabled:cursor-not-allowed
                                "
                            >
                                {busy === v.sku && <Loader2 size={11} className="animate-spin" />}
                                {v.label} · {v.price.replace("/mo", "")}
                            </button>
                        ))}
                    </div>
                ) : (
                    <button
                        type="button"
                        data-testid={`module-cta-${m.id}`}
                        disabled={busy === m.sku}
                        onClick={() => startCheckout(m.sku)}
                        className="
                            mt-6 inline-flex items-center justify-center w-full
                            font-mono-tight font-semibold uppercase tracking-[0.18em] text-[11px]
                            py-3 border border-tca-cyan/55 text-tca-cyan
                            bg-gradient-to-b from-[rgba(0,224,232,0.10)] to-transparent
                            hover:text-tca-cyan-hi hover:border-tca-cyan hover:shadow-glow-cyan-xs
                            rounded-full transition-all duration-300
                            disabled:opacity-60 disabled:cursor-not-allowed
                        "
                    >
                        {busy === m.sku && <Loader2 size={12} className="mr-2 animate-spin" />}
                        Add Module
                        {busy !== m.sku && (
                            <span className="ml-2 transition-transform group-hover:translate-x-0.5">→</span>
                        )}
                    </button>
                )}

                <div
                    aria-hidden="true"
                    className="
                        absolute bottom-0 left-[8%] right-[8%] h-px
                        bg-tca-cyan/0 group-hover:bg-tca-cyan/65
                        transition-all duration-500
                    "
                    style={{ boxShadow: "0 0 12px rgba(0, 224, 232, 0.5)" }}
                />
            </HudFrame>
        </motion.div>
    );
}
